"use client";

import * as React from "react";
import { useEffect, useState } from "react";

export function Typewriter({
  text,
  speed = 70,
  cursor = "|",
  loop = true,
  deleteSpeed = 35,
  delay = 1200,
  startDelay = 0,
  className,
  cursorClassName,
}) {
  const [displayText, setDisplayText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [textArrayIndex, setTextArrayIndex] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [hasStarted, setHasStarted] = useState(startDelay <= 0);

  const textArray = React.useMemo(
    () => (Array.isArray(text) ? text : [text]),
    [text]
  );

  const currentText = textArray[textArrayIndex] || "";

  useEffect(() => {
    setDisplayText("");
    setCurrentIndex(0);
    setIsDeleting(false);
    setTextArrayIndex(0);
    setHasStarted(startDelay <= 0);
  }, [text, startDelay]);

  useEffect(() => {
    if (!window.matchMedia) return;

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    const handleChange = () => {
      setPrefersReducedMotion(mediaQuery.matches);
    };

    handleChange();
    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    if (!currentText) return;

    if (prefersReducedMotion) {
      setDisplayText(currentText);
      setCurrentIndex(currentText.length);
      setIsDeleting(false);
      return;
    }

    if (!hasStarted) {
      const startTimeout = window.setTimeout(() => {
        setHasStarted(true);
      }, startDelay);

      return () => window.clearTimeout(startTimeout);
    }

    let deleteTimeout;

    const timeout = window.setTimeout(() => {
      if (!isDeleting) {
        if (currentIndex < currentText.length) {
          setDisplayText((prev) => prev + currentText[currentIndex]);
          setCurrentIndex((prev) => prev + 1);
        } else if (loop) {
          deleteTimeout = window.setTimeout(() => setIsDeleting(true), delay);
        }
      } else {
        if (displayText.length > 0) {
          setDisplayText((prev) => prev.slice(0, -1));
        } else {
          setIsDeleting(false);
          setCurrentIndex(0);
          setTextArrayIndex((prev) => (prev + 1) % textArray.length);
        }
      }
    }, isDeleting ? deleteSpeed : speed);

    return () => {
      window.clearTimeout(timeout);
      window.clearTimeout(deleteTimeout);
    };
  }, [
    currentIndex,
    isDeleting,
    currentText,
    loop,
    speed,
    deleteSpeed,
    delay,
    startDelay,
    displayText,
    textArray,
    prefersReducedMotion,
    hasStarted,
  ]);

  const effectiveCursorClassName = prefersReducedMotion
    ? (cursorClassName ?? "animate-pulse text-[#3BA7FF]").replace(
        /\banimate-pulse\b/g,
        ""
      )
    : cursorClassName ?? "animate-pulse text-[#3BA7FF]";

  return (
    <span className={className}>
      {displayText}
      <span className={effectiveCursorClassName}>{cursor}</span>
    </span>
  );
}
